import { Map, List } from 'immutable'
import { SET_AUTH_LOGIN_SUCCESS } from 'services/auth/actions'
import _get from 'lodash/get'
import {
  emarsysCustomerLogin,
  emarsysReload,
  emarsysTag,
  emarsysSendCartStart,
  emarsysSendCartPurchase,
  emarsysSendEmail,
} from 'services/emarsys'
import { EN } from 'services/languages/constants'
import { SET_RESOLVER_LOCATION } from 'services/resolver/actions'
import {
  SET_CHECKOUT_USER_DATA,
  SET_CHECKOUT_ORDER_DATA,
} from 'services/checkout/actions'
import {
  SET_PLANS_SELECTION,
} from 'services/plans/actions'
import { CHANGE_USER_ACCOUNT_PLAN_TYPE_SET_DATA } from 'services/user-account/actions'
import { SET_EMAIL_SIGNUP_SUCCESS } from 'services/email-signup/actions'
import { campaignShouldRender } from 'services/testarossa'
import { selectUserExternalId } from 'services/user/selectors'
import { getSrcLang } from 'services/lang'
import { selectGuestPhase, selectMembershipStatus } from 'services/user-account/selectors'
import { getPathLocationName, createAbsolute } from 'services/url'
import { get as getConfig } from 'config'
import { PERSONALITY_SAGE_ID } from 'services/oracle'
import { getAuthIsLoggedIn } from 'services/auth'
import {
  SET_EVENT_VIDEO_VIEW_QUALIFIED,
  SET_DEFAULT_GA4_EVENT,
  SET_AI_GA4_EVENT,
  SET_GA4_VARIABLES,
  SET_GA4_INIT_EVENT,
} from './actions'
import { CHECKOUT_STEP_EVENTS, sentAtDate } from '.'
import { sendEvent } from './send-event'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

const PUSH_DELAY_MS = 750
const GA4_VARIABLES_PUSH_DELAY_MS = 0

function emarsysTagVideoQualifiedView (store) {
  const { inboundTracking = Map(), video = Map() } = store.getState()
  const scSrc = inboundTracking.getIn(['data', 'sc_src'])
  const scLid = inboundTracking.getIn(['data', 'sc_llid'])

  if (scSrc) {
    const campaignId = scSrc.substring(scSrc.lastIndexOf('_') + 1)
    if (campaignId) {
      const videoId = video.getIn(['data', 'id'])
      const timestamp = Date.now()
      let tag = `${campaignId}|${videoId}|${timestamp}`
      if (scLid) {
        const variantId = scLid
        tag = `${campaignId}-${variantId}|${videoId}|${timestamp}`
      }
      emarsysTag(tag)
    }
  }
}

function emarsysSendCartStartData (store, action) {
  let { email } = action.payload
  const { plans = Map(), user = Map() } = store.getState()
  const selectedPlan = plans.get('selection')

  if (user.getIn(['data', 'mail'])) {
    email = user.getIn(['data', 'mail'])
  }

  if (!email || !Map.isMap(selectedPlan)) {
    return
  }

  const id = selectedPlan.get('id')
  const price = parseFloat(selectedPlan.getIn(['costs', 0], 0))
  const cartItems = [
    {
      item: id,
      quantity: 1,
      price,
    },
  ]

  emarsysSendCartStart(cartItems, email)
}

function emarsysSendCartPurchaseData (store, action) {
  const { billing, billingResponse, email } = action.payload
  const { plans } = store.getState()
  const selectedPlan = plans.get('selection')
  const id = selectedPlan.get('id')
  const price = parseFloat(selectedPlan.getIn(['costs', 0], 0))
  const purchase = {
    orderId: _get(billing, ['orderNumber']) || _get(billingResponse, 'orderNumber'),
    items: [
      {
        item: id,
        quantity: 1,
        price,
      },
    ],
  }

  emarsysSendCartPurchase(purchase, email)
}

function emarsysSendChangePlanData (store, action) {
  const orderNumber = Map.isMap(action.payload)
    ? action.payload.get('orderNumber')
    : ''

  if (!orderNumber) {
    return
  }

  const { plans, user } = store.getState()
  const selectedPlanSku = plans.getIn(['changePlanData', 'selectedPlan'])
  const availablePlans = plans.getIn(['data', 'plans'], List())
  const fullSelectedPlan = planGraphQLActive ? plans.getIn(['changePlanData', 'selectedPlan'], Map()) || Map() : availablePlans.find((plan) => plan.get('sku') === selectedPlanSku) || Map()
  const id = fullSelectedPlan.get('id', '')
  const price = parseFloat(fullSelectedPlan.getIn(['costs', 0], 0))
  const email = user.getIn(['data', 'mail'], '') || user.getIn(['data', 'email'], '')
  const purchase = {
    orderId: orderNumber,
    items: [
      {
        item: id,
        quantity: 1,
        price,
      },
    ],
  }

  emarsysSendCartPurchase(purchase, email)
}

function emarsysSendEmailSignupData (action) {
  const { email, success } = action.payload
  if (success) {
    emarsysSendEmail(email)
  }
}

function getTestarossaTestsData (store) {
  const { testarossa } = store.getState()

  if (Map.isMap(testarossa) && testarossa.get('initialized')) {
    // Note: Testarossa records return repeated elements
    const testarossaRecords = testarossa
      .get('records')
      .filter((record) => {
        const campaign = record.getIn(['campaign', 'friendlyId'])
        return campaignShouldRender({ campaign })
      })
      .toSet() // convert to Set to remove repeated elements
      .toList() // convert back to immutable List

    return testarossaRecords.map((record, index) => {
      const name = record.getIn(['campaign', 'friendlyId'])
      const variant = record.getIn(['variation', 'friendlyId'])
      return {
        [`test_name_${index}`]: name,
        [`test_variant_${index}`]: variant,
      }
    })
  }

  return new List()
}

function getUserTestsData (store) {
  const { optimizely, optimizelyFeature } = store.getState()
  const experiments = optimizely.get('experiments')
  const flags = optimizelyFeature.get('flags')
  let userTests = getTestarossaTestsData(store)

  // optimizely web experimentation (OLD)
  if (Map.isMap(experiments) && experiments.size > 0) {
    experiments.forEach((experiment) => {
      const index = userTests.size
      const name = experiment.get('campaignName') || experiment.get('experimentId')
      const variant = experiment.get('variationId')

      userTests = List([...userTests, {
        [`test_name_${index}`]: name,
        [`test_variant_${index}`]: variant,
      }])
    })
  }

  // optimizely feature experimentation
  if (Map.isMap(flags) && flags.size > 0) {
    flags.forEach((attributes, flag) => {
      const index = userTests.size
      const enabled = !!attributes.get('enabled')
      const variant = attributes.get('variation')

      if (enabled) {
        userTests = List([...userTests, {
          [`test_name_${index}`]: flag,
          [`test_variant_${index}`]: variant,
        }])
      }
    })
  }

  return userTests
}

// GA4 - Global/Default event
function pushGa4DefaultEvent (store, action) {
  const { event = Map() } = action.payload
  const { dataLayer } = global
  const state = store.getState()
  if (Map.isMap(event) && !event.isEmpty()) {
    const {
      app,
      auth,
      user,
      resolver,
      upstreamContext = Map(),
    } = state

    // get all tests data and collate it
    const allTests = getUserTestsData(store)
    const allTestsCollated = allTests.map((test2, index) => {
      return `${test2[`test_name_${index}`]} Var ${test2[`test_variant_${index}`]}`
    }).join(', ')

    const isLoggedIn = getAuthIsLoggedIn(auth)
    const userId = event.get('user_id') || user.getIn(['data', 'uid']) || undefined
    const path = resolver.get('path')
    const membershipStatus = event.get('membership_status') || selectMembershipStatus(state)
    const guestPhase = selectGuestPhase(state)
    const externalId = selectUserExternalId(state)
    const timestamp = sentAtDate()
    const userLanguage = user.getIn(['data', 'language', 0]) || EN
    const uiElement = event.get('ui_element')
    const mediaLanguage = event.get('media_language')
    const sharePageAttribution = upstreamContext && upstreamContext.getIn(['data', 'sharePageAttribution'])
    let data = event
      .mergeDeep(Array.from(allTests).reduce((accumulatedMap, test) => {
        return new Map([...accumulatedMap, ...new Map(test)])
      }, new Map()))
      .set('guest_phase', guestPhase)
      .set('membership_status', membershipStatus)
      .set('app_name', 'web-app')
      .set('app_version', app.get('version'))
      .set('user_in_test', allTests.size > 0)
      .set('all_tests_collated', allTestsCollated)
      .set('user_language', userLanguage)
      .set('current_url', path)
      .set('gaia_uuid', externalId)
      .set('event_time', timestamp)
      .set('event_number', dataLayer.length)

    if (isLoggedIn && userId) {
      data = data.set('user_id', userId)
    }

    if (uiElement) {
      data = data.mergeDeep({
        ui_location: event.get('ui_location') || getPathLocationName(path),
        ui_element: uiElement,
      })
    }

    if (mediaLanguage) {
      data = data.mergeDeep({
        media_language: getSrcLang(mediaLanguage) || getSrcLang(userLanguage),
      })
    }

    // Add checkout events data from the upstreamContext to checkout events
    if (sharePageAttribution && CHECKOUT_STEP_EVENTS.includes(event.get('event'))) {
      data = data.mergeDeep(sharePageAttribution)
    }

    setTimeout(() => {
      sendEvent(store, data)
    }, PUSH_DELAY_MS)
  }
}

// GA4 - IA event
function pushAiGa4Event (store, action) {
  const { event = Map() } = action.payload
  const { dataLayer } = global
  const state = store.getState()
  if (Map.isMap(event) && !event.isEmpty()) {
    const {
      app,
      auth,
      page,
      user,
      oracle,
      resolver,
    } = state

    // get all tests data and collate it
    const allTests = getUserTestsData(store)
    const allTestsCollated = allTests.map((test2, index) => {
      return `${test2[`test_name_${index}`]} Var ${test2[`test_variant_${index}`]}`
    }).join(', ')

    const isLoggedIn = getAuthIsLoggedIn(auth)
    const userId = event.get('user_id') || user.getIn(['data', 'uid']) || undefined
    const path = resolver.get('path')
    const membershipStatus = event.get('membership_status') || selectMembershipStatus(state)
    const guestPhase = selectGuestPhase(state)
    const externalId = selectUserExternalId(state)
    const timestamp = sentAtDate()
    const userLanguage = user.getIn(['data', 'language', 0]) || EN
    const uiLocation = event.get('ui_location') || getPathLocationName(path)
    const aiPersona = event.get('ai_persona') || oracle.get('persona') || PERSONALITY_SAGE_ID

    let data = event
      .mergeDeep(Array.from(allTests).reduce((accumulatedMap, test) => {
        return new Map([...accumulatedMap, ...new Map(test)])
      }, new Map()))
      .set('page_path', path)
      .set('page_title', page.get('title'))
      .set('ui_location', uiLocation)
      .set('current_url', createAbsolute(path))
      .set('app_name', 'web-app')
      .set('app_version', app.get('version'))
      .set('all_tests_collated', allTestsCollated)
      .set('event_time', timestamp)
      .set('event_number', dataLayer.length)
      .set('membership_status', membershipStatus)
      .set('user_in_test', allTests.size > 0)
      .set('user_language', userLanguage)
      .set('guest_phase', guestPhase)
      .set('gaia_uuid', externalId)
      .set('ai_persona', aiPersona)

    if (isLoggedIn && userId) {
      data = data.set('user_id', userId)
    }

    setTimeout(() => {
      sendEvent(store, data)
    }, PUSH_DELAY_MS)
  }
}

function pushGa4Variables (store) {
  const state = store.getState()
  const { dataLayer } = global
  const {
    app,
    user,
    resolver,
  } = state

  const externalId = selectUserExternalId(state)
  const userLanguage = user.getIn(['data', 'language', 0]) || EN
  const membershipStatus = selectMembershipStatus(state)
  const guestPhase = selectGuestPhase(state)

  const data = {
    guest_phase: guestPhase,
    membership_status: membershipStatus,
    user_id: user.getIn(['data', 'uid']) || undefined,
    user_language: userLanguage,
    current_url: resolver.get('path'),
    app_name: app.get('name', 'web-app'),
    app_version: app.get('version'),
    gaia_uuid: externalId,
  }

  setTimeout(() => {
    dataLayer.push(data)
  }, GA4_VARIABLES_PUSH_DELAY_MS)
}

function pushGa4InitEvent () {
  const { dataLayer } = global

  const data = {
    event: 'gtm.init',
  }

  setTimeout(() => {
    dataLayer.push(data)
  }, PUSH_DELAY_MS)
}

/**
 * Redux middleware
 * @param {import('redux').Store} store A redux store
 * @returns {import('redux').Middleware} Redux middleware
 */
export default function middleware (store) {
  return (next) => (action) => {
    // dataLayer is special needs care
    switch (action.type) {
      case SET_DEFAULT_GA4_EVENT:
        pushGa4DefaultEvent(store, action)
        break
      case SET_AI_GA4_EVENT:
        pushAiGa4Event(store, action)
        break
      case SET_GA4_VARIABLES:
        pushGa4Variables(store, action)
        break
      case SET_GA4_INIT_EVENT:
        pushGa4InitEvent()
        break
      default:
        // Do nothing.
        break
    }

    // Tracking
    switch (action.type) {
      case SET_AUTH_LOGIN_SUCCESS:
        emarsysCustomerLogin(store)
        break
      case SET_RESOLVER_LOCATION:
        // Needed to reload Emarsys for Web Channel due to SPA
        emarsysReload()
        break
      case SET_EVENT_VIDEO_VIEW_QUALIFIED:
        emarsysTagVideoQualifiedView(store)
        break
      case SET_CHECKOUT_USER_DATA:
      case SET_PLANS_SELECTION:
        setTimeout(() => {
          emarsysSendCartStartData(store, action)
        }, 500)
        break
      case SET_CHECKOUT_ORDER_DATA:
        setTimeout(() => {
          emarsysSendCartPurchaseData(store, action)
        }, 500)
        break
      case CHANGE_USER_ACCOUNT_PLAN_TYPE_SET_DATA:
        emarsysSendChangePlanData(store, action)
        break
      case SET_EMAIL_SIGNUP_SUCCESS:
        emarsysSendEmailSignupData(action)
        break
      default:
        // do nothing
        break
    }
    next(action)
  }
}
